'use client';
import { useState, useTransition, type JSX } from 'react';
import { Button } from '@/components/ui/button';
import { HiArrowTurnRightDown } from 'react-icons/hi2';
import { useLocale } from 'next-intl';
import { FaTag } from 'react-icons/fa6';
import { Link } from '@/i18n/navigation';
import Image from 'next/image';
import { cn } from '@/lib/utils';
import type { PaginatedDocs } from 'payload';
import type { TipsPost } from '@/payload-types';

export function MoreTipsList({
  take,
  isButtonHidden,
  tagSlugs,
}: {
  take: number;
  isButtonHidden: boolean;
  tagSlugs?: string[];
}): JSX.Element {
  const [isButtonVisible, setIsButtonVisible] = useState<boolean>(isButtonHidden);
  const [pageNumber, setPageNumber] = useState<number>(2);
  const [tipses, setTipses] = useState<PaginatedDocs<TipsPost>['docs']>([]);
  const [isPending, startTransition] = useTransition();
  const locale = useLocale() as Locales;

  async function loadMoreToggle(): Promise<void> {
    const searchParams = new URLSearchParams();
    searchParams.set('page', pageNumber.toString());
    searchParams.set('limit', take.toString());
    searchParams.set('locale', locale);
    searchParams.set('deps', '3');
    if (tagSlugs && tagSlugs.length > 0) {
      searchParams.set('where[tags.slug][in]', tagSlugs.join(','));
    }
    const response = await fetch(`/api/tips-posts?${searchParams.toString()}`);

    if (response.ok) {
      const data = (await response.json()) as PaginatedDocs<TipsPost>;
      setTipses((prevTipses) => [...prevTipses, ...data.docs]);
      setPageNumber((prevPage) => prevPage + 1);
      setIsButtonVisible(data.hasNextPage);
    }
  }

  return (
    <>
      {tipses.length > 0 &&
        tipses.map((tips, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: 「もっと見る」で末尾に追記するのみ。既存要素の位置は変わらない
          <article key={index} className="w-full lg:w-1/2 2xl:w-1/3 p-2 flex">
            <Link
              href={`/tips/${tips.slug}`}
              className="flex flex-col flex-1 p-2 rounded-sm bg-stone-100 dark:bg-stone-800 border-2 border-solid border-transparent transition-colors hover:border-stone-300 dark:hover:border-stone-600"
            >
              <h3 title={tips.title} className="text-lg font-semibold line-clamp-2 mb-2">
                {tips.title}
              </h3>
              <p className="text-sm text-muted-foreground line-clamp-4">{tips.excerpt}</p>
              {tips.tags && (
                <ul className="w-full mt-2">
                  {tips.tags.map((tag, idx) => (
                    <li
                      // biome-ignore lint/suspicious/noArrayIndexKey: サーバーコンポーネントが毎回全件を描画する固定リスト。並べ替え・挿入・削除が起きない（各 Tips のタグ）
                      key={idx}
                      className="inline-flex flex-row items-center justify-end gap-x-1 text-xs bg-stone-300 dark:bg-stone-700 text-stone-800 dark:text-stone-200 rounded-full px-2 py-1 mr-1 mt-2"
                    >
                      {typeof tag === 'object' && typeof tag.icon === 'object' && tag.icon?.url ? (
                        <Image
                          width={30}
                          height={30}
                          src={tag.icon.url}
                          alt={locale === 'ja' ? '"アイコン"' : 'icon'}
                          className="size-[1.3em] rounded-full"
                        />
                      ) : (
                        <FaTag aria-label={locale === 'ja' ? '"アイコン"' : 'icon'} />
                      )}
                      {typeof tag === 'object' ? tag.name : tag}
                    </li>
                  ))}
                </ul>
              )}
            </Link>
          </article>
        ))}
      {isButtonVisible && (
        <div className="flex items-center justify-center py-4">
          <Button
            type="button"
            variant={'secondary'}
            className="!px-7"
            aria-label={locale === 'ja' ? 'もっと見る' : 'Load More'}
            onClick={() => startTransition(loadMoreToggle)}
            disabled={isPending || !isButtonVisible}
          >
            {isPending && (
              <span
                className={cn([
                  'animate-spin size-[1.2em] block transition-opacity',
                  'rounded-full border-4 border-solid',
                  'border-slate-700 dark:border-slate-500 !border-t-transparent',
                ])}
              ></span>
            )}
            {locale === 'ja' ? 'もっと見る' : 'Load More'}
            <HiArrowTurnRightDown />
          </Button>
        </div>
      )}
    </>
  );
}
